import { NextPage } from 'next'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { Seo } from '@/layout/Seo'
import { useMetaForProject } from '@/layout/Seo/ProjectSeoHelper'
import { useProjectVideos } from '@/organisms/VideoHorizontalScroller'
import { useGuildMember } from '@/services/GuildUserService'
import { getMovieAndSpecialsWatchables, getProjectMeta, ProjectMeta } from '@/services/ProjectsService'
import { Media, processWatchablesToMediasWithFiltering } from '@/utils/EpisodeUtil'
import { logger } from '@/utils/logging'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { isDefined } from '@/utils/primitives/generics'
import { loadTranslations } from '@/utils/translate/translate-server'
import { WatchProjectBonusContentView, WatchProjectBonusContentViewProps } from '@/views/WatchProjectBonusContentView'

interface WatchProjectBonusContentPageProps
  extends Omit<WatchProjectBonusContentViewProps, 'userHasEarlyAccess' | 'bonusVideos'> {
  projectMeta: ProjectMeta
  locale: string
  movieSpecialMedias: Media[]
}

export const WatchProjectBonusContentPage: NextPage<WatchProjectBonusContentPageProps> = ({
  projectMeta,
  movieSpecialMedias,
  locale,
  hasWatchableReasons,
}) => {
  const meta = useMetaForProject({ ...projectMeta, locale })
  const { guildPermissions } = useGuildMember()
  const userHasEarlyAccess = Boolean(guildPermissions.hasEarlyAccess)
  const { videos: bonusVideos } = useProjectVideos(projectMeta.slug, 'bonus')

  const title = `${projectMeta.name} Bonus Content | Behind the Scenes, Extras, & Previews`
  const description = `Watch behind-the-scenes clips, bonus videos, and exclusive previews from ${projectMeta.name}.  Discover how your favorite stories came to life.`

  return (
    <ProjectMetaProvider {...projectMeta}>
      <Seo {...meta} title={title} description={description} />
      <WatchProjectBonusContentView
        bonusVideos={bonusVideos?.filter(isDefined) ?? []}
        movieSpecialMedias={movieSpecialMedias}
        projectMeta={projectMeta}
        userHasEarlyAccess={userHasEarlyAccess}
        hasWatchableReasons={hasWatchableReasons}
      />
    </ProjectMetaProvider>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<WatchProjectBonusContentPageProps>(
  async ({ federationClient, locale, resolvedUrl, params, next404 }) => {
    try {
      const translations = await loadTranslations(locale, ['common', 'watch'])
      const projectSlug = params?.project as string

      if (!projectSlug) {
        logger().error('Project slug is required', { resolvedUrl })
        return next404()
      }

      const projectMeta = await getProjectMeta({ slug: projectSlug }, federationClient, { locale })

      if (!projectMeta) {
        logger().warn('Project not found', { projectSlug, resolvedUrl })
        return next404()
      }

      const { watchables } = await getMovieAndSpecialsWatchables(projectSlug, federationClient)

      const movieSpecialMedias = processWatchablesToMediasWithFiltering(
        watchables,
        {
          projectName: projectMeta?.name,
          hasEarlyAccess: false,
          projectType: projectMeta?.projectType,
          trailerText: 'Trailer',
          locale,
        },
        false,
        false,
      )

      const isMovieOrSpecial = projectMeta?.projectType === 'movie' || projectMeta?.projectType === 'special'
      let filteredMovieSpecialMedias = movieSpecialMedias

      if (isMovieOrSpecial && movieSpecialMedias.length > 0) {
        const primaryMedia = movieSpecialMedias.find((m) => {
          const title = (m.title ?? '').toLowerCase()
          return (
            m.guid &&
            !title.includes('trailer') &&
            !title.includes('teaser') &&
            !title.includes('sneak peek') &&
            !title.includes('sneakpeek')
          )
        })

        // Filter out the primary movie/special if found
        if (primaryMedia) {
          filteredMovieSpecialMedias = movieSpecialMedias.filter((media) => media.guid !== primaryMedia.guid)
        }
      }

      // Handle episodeNumber returning undefined and causing serializable error
      const sanitizedMedias = JSON.parse(JSON.stringify(filteredMovieSpecialMedias)) as Media[]

      const hasWatchableReasons = filteredMovieSpecialMedias.some(
        (media) => media.watchableAvailabilityStatus && media.watchableAvailabilityStatus !== 'WATCHABLE',
      )

      return {
        props: {
          projectMeta,
          movieSpecialMedias: sanitizedMedias,
          locale,
          hasWatchableReasons,
          ...translations,
        },
      }
    } catch (error) {
      logger().error(`Error fetching bonus content page data for ${resolvedUrl}`, { error })
      throw error
    }
  },
)

export default WatchProjectBonusContentPage
